import cn from 'clsx';
import React from 'react';

import { isIE } from '../__utils__/browserUtils';
import { FCC } from '../__utils__/types';
import { LocalizedProps, useIntl } from '../i18n';
import T from '../Typography';
import SvgB from './b.svg';
import * as messages from './i18n';
import type { MessageId } from './i18n';
import { getCircle, StyledProgressSpinner } from './ProgressSpinner.styles';
import { ProgressSpinnerProps, ProgressSpinnerSize } from './ProgressSpinner.types';

namespace ProgressSpinner {
    export type Props = ProgressSpinnerProps;
}

const sizeToLabel = {
    small: T.BodySmall,
    medium: T.Body,
    large: T.DataDisplay2,
    xlarge: T.DataDisplay1,
};

const SIZING = {
    small: 22,
    medium: 40,
    large: 60,
    xlarge: 138,
} as const;

const ARC_SIZE_MAX = 300; // degrees
const ARC_SIZE_MIN = 20; // degrees
const calcRadius = (size: ProgressSpinnerSize) => 9 * (SIZING[size] / 20.0);

/**
 * A dynamic spinning animation to indicate loading/processing.
 */
const ProgressSpinner: FCC<LocalizedProps<MessageId, ProgressSpinnerProps>> = ({
    className,
    label,
    size = 'small',
    labelPosition = 'bottom',
    // Aria attributes should be set according to WAI-ARIA spec, even though spinner is indeterminate
    'aria-valuemin': ariaValuemin = 0,
    'aria-valuemax': ariaValuemax = 1,
    locale,
    messages: messagesOverride,
    ...rest
}) => {
    const isBrowserIE = isIE();
    const Label = sizeToLabel[size];

    const { intl } = useIntl(messages, locale, messagesOverride);
    label =
        label ??
        `${intl.formatMessage({
            id: 'bbui-ProgressSpinner-label',
            defaultMessage: messages['en']['bbui-ProgressSpinner-label'],
        })}…`;

    const width = `${SIZING[size]}px`;
    const height = `${SIZING[size]}px`;
    const radius = calcRadius(size);

    const cssVars: React.CSSProperties = {
        '--_Progress-spinner-width': width,
        '--_Progress-spinner-height': height,
        '--_Progress-spinner-pi': Math.PI,
        '--_Progress-spinner-radius': `${radius}px`,
        '--_Progress-spinner-arc-size-max': ARC_SIZE_MAX,
        '--_Progress-spinner-arc-size-min': ARC_SIZE_MIN,
    };

    return (
        <StyledProgressSpinner
            className={cn('bbui-progress-spinner', className)}
            role="progressbar"
            aria-valuemin={ariaValuemin}
            aria-valuemax={ariaValuemax}
            {...rest}
            style={{
                ...cssVars,
                ...rest?.style,
            }}
            data-label-position={labelPosition}
        >
            <div className="spinner">
                <div className="rotation">
                    <div className={cn('oscillation', { ie: isBrowserIE })}>{getCircle(size)}</div>
                </div>
            </div>
            {label && (
                <Label as="div" className="bbui-progress-spinner-label">
                    {label}
                </Label>
            )}
            {size === 'xlarge' && <SvgB className="bbui-progress-spinner-b" />}
        </StyledProgressSpinner>
    );
};

export default ProgressSpinner;
